/**
 * BlockSuite pre-initialization module.
 * This module is imported at the top of main.tsx so that BlockSuite
 * effects, collection creation, and doc initialization all happen
 * synchronously / eagerly BEFORE React renders the first frame.
 *
 * By the time DesignStudioEditor mounts, `getPrebuiltEditor()` returns
 * a fully initialized editor element — eliminating the loading screen.
 */

import { effects as itEffects } from '@blocksuite/integration-test/effects';
import { getTestStoreManager } from '@blocksuite/integration-test/store';
import { effects as commentEffects } from '../../comment/effects.js';
import {
  createStarterDocCollection,
  initStarterDocCollection,
} from '../../starter/utils/collection.js';
import { createTestEditor } from '../../starter/utils/extensions.js';
import { listenHashChange } from '../../_common/history.js';
import * as store from '@blocksuite/affine/store';

// Run side-effect registrations immediately (idempotent)
itEffects();
commentEffects();

const storeManager = getTestStoreManager();

// Shared singleton collection — same one used by both page & edgeless modes
export const sharedCollection = createStarterDocCollection(storeManager);

// The pre-built editor element (null until init promise resolves)
let _prebuiltEditor: any = null;
let _initDone = false;

/**
 * Returns the pre-built editor if init has completed, otherwise null.
 * DesignStudioEditor uses this to skip the loading state.
 */
export function getPrebuiltEditor(): any {
  return _prebuiltEditor;
}

export function isInitDone(): boolean {
  return _initDone;
}

// The single init promise — multiple callers await the same work
export const collectionInitPromise: Promise<void> = (async () => {
  try {
    await initStarterDocCollection(sharedCollection);

    // Create a distinct document for the Endless Canvas if it does not exist
    let canvasDoc = sharedCollection.getDoc('doc:canvas');
    if (!canvasDoc) {
      canvasDoc = sharedCollection.createDoc('doc:canvas');
      const canvasStore = canvasDoc.getStore();
      canvasDoc.load(() => {
        const rootId = canvasStore.addBlock('affine:page', {
          title: new store.Text('Endless Canvas Workspace'),
        });
        canvasStore.addBlock('affine:surface', {}, rootId);
        const noteId = canvasStore.addBlock('affine:note', {}, rootId);
        canvasStore.addBlock('affine:paragraph', {}, noteId);
      });
      canvasStore.resetHistory();
    }

    // Default editor to mount home document initially
    const firstDoc = sharedCollection.getDoc('doc:home') || sharedCollection.docs.values().next().value;
    if (firstDoc) {
      const docStore = firstDoc.getStore();
      docStore.load();
      _prebuiltEditor = createTestEditor(docStore, sharedCollection);
      // Kick off the LitElement upgrade (no need to await — it will finish
      // before the component tries to appendChild it because the microtask
      // queue flushes synchronously during the await in DesignStudioEditor)
      _prebuiltEditor.updateComplete.catch(console.error);
      listenHashChange(sharedCollection, _prebuiltEditor);
    }
  } catch (err) {
    console.error('BlockSuite pre-init failed:', err);
  } finally {
    _initDone = true;
  }
})();
